/**
 * PredictionAccuracyPanel Component
 *
 * Collapsible panel showing per-strain forecast accuracy metrics.
 * Displays MAPE, bias direction, and calibration trend from
 * the usePredictionAccuracy hook.
 *
 * US-C2-009: Prediction Accuracy Panel
 */

import { useState } from 'react'
import { usePredictionAccuracy } from '@/hooks/usePredictionAccuracy'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  ChevronDown,
  ChevronUp,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Format MAPE as accuracy percentage (100 - MAPE)
 * @param {number|null} mape - Mean Absolute Percentage Error
 * @returns {string} Formatted accuracy string
 */
function formatAccuracy(mape) {
  if (mape == null) return 'N/A'
  const accuracy = Math.max(0, 100 - mape)
  return `${accuracy.toFixed(1)}%`
}

/**
 * Get colour class based on accuracy level
 * @param {number|null} mape - Mean Absolute Percentage Error
 * @returns {string} Tailwind colour class
 */
function getAccuracyColour(mape) {
  if (mape == null) return 'text-gray-500'
  if (mape <= 10) return 'text-green-700'
  if (mape <= 20) return 'text-amber-700'
  return 'text-red-700'
}

/**
 * Get badge variant based on accuracy level
 * @param {number|null} mape - Mean Absolute Percentage Error
 * @returns {string} Badge variant
 */
function getAccuracyBadgeVariant(mape) {
  if (mape == null) return 'outline'
  if (mape <= 10) return 'default'
  if (mape <= 20) return 'secondary'
  return 'destructive'
}

/**
 * Get bias icon and label
 * @param {number|null} bias - Bias value (positive = over-predicting)
 * @returns {{ icon: JSX.Element, label: string, colour: string }}
 */
function getBiasInfo(bias) {
  if (bias == null) return { icon: <Minus className="h-3 w-3" />, label: 'No data', colour: 'text-gray-500' }
  if (bias > 5) return { icon: <TrendingUp className="h-3 w-3" />, label: 'Over-predicting', colour: 'text-blue-700' }
  if (bias < -5) return { icon: <TrendingDown className="h-3 w-3" />, label: 'Under-predicting', colour: 'text-amber-700' }
  return { icon: <Minus className="h-3 w-3" />, label: 'Balanced', colour: 'text-green-700' }
}

export default function PredictionAccuracyPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const { data: metrics, loading, error } = usePredictionAccuracy({ weeks: 12 })

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-between px-4 py-3 h-auto hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-900">
                Prediction Accuracy
              </span>
              {metrics && metrics.length > 0 && (
                <Badge variant="outline" className="text-xs ml-1">
                  {metrics.length} strain{metrics.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 border-t">
            {loading && (
              <div className="space-y-3 pt-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}

            {error && (
              <div className="pt-4 flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && (!metrics || metrics.length === 0) && (
              <p className="pt-4 text-sm text-gray-500 italic">
                No prediction data available yet. Accuracy metrics appear once forecasts have actual sales to compare against.
              </p>
            )}

            {!loading && !error && metrics && metrics.length > 0 && (
              <div className="pt-4">
                {/* Table header */}
                <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider pb-2 border-b">
                  <div className="col-span-1">Strain</div>
                  <div className="col-span-1 text-right">Accuracy</div>
                  <div className="col-span-1 text-right">MAPE</div>
                  <div className="col-span-1 text-center">Bias</div>
                  <div className="col-span-1 text-right">Calibration</div>
                </div>

                {/* Table rows */}
                <div className="divide-y">
                  {metrics.map(metric => {
                    const biasInfo = getBiasInfo(metric.bias)
                    const calibrationImprovement = metric.calibration_improvement

                    return (
                      <div
                        key={metric.strain_id}
                        className="grid grid-cols-5 gap-2 py-2.5 items-center text-sm"
                      >
                        {/* Strain name */}
                        <div className="col-span-1 font-medium text-gray-900 truncate" title={metric.strain_name}>
                          {metric.strain_name}
                        </div>

                        {/* Accuracy (100 - MAPE) */}
                        <div className={cn('col-span-1 text-right font-semibold', getAccuracyColour(metric.mape))}>
                          {formatAccuracy(metric.mape)}
                        </div>

                        {/* Raw MAPE */}
                        <div className="col-span-1 text-right text-gray-600">
                          {metric.mape != null ? `${metric.mape.toFixed(1)}%` : 'N/A'}
                        </div>

                        {/* Bias direction */}
                        <div className={cn('col-span-1 flex items-center justify-center gap-1', biasInfo.colour)}>
                          {biasInfo.icon}
                          <span className="text-xs hidden sm:inline">{biasInfo.label}</span>
                        </div>

                        {/* Calibration improvement */}
                        <div className="col-span-1 text-right">
                          {calibrationImprovement != null ? (
                            <Badge
                              variant={calibrationImprovement > 0 ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {calibrationImprovement > 0 ? '+' : ''}{calibrationImprovement.toFixed(1)}%
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Footer summary */}
                <div className="pt-3 mt-2 border-t text-xs text-gray-500">
                  Based on last 12 weeks of data. Calibration shows improvement over raw predictions.
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
